import { ReactNode, useEffect, useState } from "react";
import { FuzzyMatcher } from "../../models/matchers/FuzzyMatcher";
import { Matcher } from "../../models/matchers/Matcher";
import { RegexMatcher } from "../../models/matchers/RegexMatcher";
import { LocationMatcher } from "../../models/matchers/LocationMatcher";
import { ExpandingTooltip } from "../tooltips/ExpandingTooltip";
import { FuzzyMatcherEditor } from "./fuzzy/FuzzyMatcherEditor";
import { RegexMatcherEditor } from "./regex/RegexMatcherEditor";
import { LocationMatcherEditor } from "./location/LocationMatcherEditor";
import { MatcherTypes } from "../../types/rules/MatcherTypes";
import { Hydratable } from "../../models/hydration/Hydratable";
import AcceleratorDoc from "../../models/AcceleratorDoc";

export { MatcherEditor, MatcherEditorChildProps, Props };

/**
 * Define props child components may received from the MatcherEditor component.
 * TODO: update jdoc
 */
type Props<T extends Matcher> = {
  defaultMatcherThreshold: number;
  matcher: T;
  update(matcher: T, oldMatcherId: string): void;
  /**
   * If defined, the component allows for the MatcherType to be changed.
   */
  changedMatcherType?: (matcher: Matcher, oldMatcherId: string) => void;
  /**
   * Filter the matcher types allowed to be changed into.
   */
  filter?: MatcherTypes[];
  /**
   * Add a button component to the MatcherEditor.
   */
  button?: ReactNode;
  /**
   * Text to display in MatcherEditor label
   */
  label?: string;
  /**
   * Text Color of MatcherEditor label
   */
  labelColor?: string;
  /**
   * Background Colour of MatcherEditor label
   */
  labelBgColor?: string;
  /**
   * Tooltip for label in MatcherEditor
   */
  labelTooltip?: ExpandingTooltip;
  /**
   * onClick handler for MatcherEditor label
   */
  onClickLabel?(): void;
  /**
   * TODO: unused ?
   */
  index?: number;
  acceleratorDoc?: Hydratable<AcceleratorDoc>;
  pageIndex?: number;
  active?: boolean;
  setActive?: (active: boolean) => void;
};

type MatcherEditorChildProps<T extends Matcher> = Omit<
  Props<T>,
  "update" | "changedMatcherType"
> & {
  update(matcher: T): void;
  changedMatcherType?: (matcher: Matcher) => void;
};

const MatcherEditor = (props: Props<Matcher>) => {
  const [oldMatcherId, setOldMatcherId] = useState(
    props.matcher.getMatcherId(),
  );

  // update oldMatcherId when matcher prop changes
  useEffect(
    () => setOldMatcherId(props.matcher.getMatcherId()),
    [props.matcher],
  );

  const update = (matcher: Matcher) => props.update(matcher, oldMatcherId);

  const changeMatcherType = props.changedMatcherType
    ? (matcher: Matcher) => props.changedMatcherType!(matcher, oldMatcherId)
    : undefined;

  switch (props.matcher.getDiscriminator()) {
    case "M_REGEX": {
      return (
        <RegexMatcherEditor
          {...props}
          matcher={props.matcher as RegexMatcher}
          update={update}
          changedMatcherType={changeMatcherType}
        />
      );
    }
    case "M_FUZZY": {
      return (
        <FuzzyMatcherEditor
          {...props}
          matcher={props.matcher as FuzzyMatcher}
          update={update}
          changedMatcherType={changeMatcherType}
        />
      );
    }
    case "M_LOCATION": {
      return (
        <LocationMatcherEditor
          {...props}
          matcher={props.matcher as LocationMatcher}
          update={update}
          changedMatcherType={changeMatcherType}
        />
      );
    }
    default: {
      return null;
    }
  }
};
